import * as React from 'react';

import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import FormEditingHandler from 'Containers/FormEditingHandler';
import DiagnosticSurveyForm from './Form';
import NPIHistory from './HistoryRowData/NPI';
import FBIMODHistory from './HistoryRowData/FBIMOD';
import BNSQHistory from './HistoryRowData/BNSQ';
import { FormContextProvider } from 'Containers/FormContextHandler';
import { InformationButton } from '../components';
import { npiFormInformation } from './Form/NPI/definitions';
import ADLHistory from './HistoryRowData/ADL';
import IADLHistory from './HistoryRowData/IADL';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';
import { isInformantSurvey } from 'Utility/informants';
import { isMyDocumentType } from 'neuro-schemas';
import { useAppSelector } from 'Store/index';
import { actions as myAppActions } from 'Store/myapp/actions';
import { useDispatch } from 'react-redux';
import StandAloneFormEditingHandler from 'Containers/StandAloneFormEditingHandler';
import QuestionAnswerPair from 'Components/_NewElements/QuestionAnswerPair';
import { useIntl } from 'react-intl';
import { surveyCreator, surveyEditor } from 'Routes/MyService/util/index';
import { useLocation, useNavigate } from 'react-router';
import type { TDiagnosticSurveyDocumentType, TDiagnosticSurveyMyDocumentType, TRelevantDocumentType } from '../utils';

const diagnosticSurveyTypes: TDiagnosticSurveyDocumentType[] = ['npi', 'fbimod', 'bnsq', 'adl', 'iadl'];

const isMyDiagnosticSurveyDocument = (d: unknown): d is IDocumentWithTypeV2<TDiagnosticSurveyMyDocumentType> => {
  if (typeof d !== 'object' || d === null || !('_type' in d) || !('_id' in d)) return false;
  return diagnosticSurveyTypes.some(type => type === d._type) && isMyDocumentType(d._type);
};

const docType = (
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>,
  editing: string,
  view?: IView,
): TDocumentType | undefined =>
  editing
    ? documents.find(d => d._id === editing)?._type
    : view?.viewing
      ? documents.find(d => d._id === view.viewing)?._type
      : undefined;

const DiagnosticSurveyHistory = (
  documents: Array<IDocumentWithTypeV2<TDiagnosticSurveyDocumentType>>,
  startEdit: StartEdit<IDocumentWithTypeV2<TDiagnosticSurveyDocumentType>>,
  handleMyDelete: (myDocId: string, docType: TMyDocumentType) => Promise<void>,
  view?: IView,
): React.JSX.Element => {
  return (
    <React.Fragment>
      <NPIHistory documents={filterByDocumentType(documents, 'npi')} startEdit={startEdit} view={view} />
      <FBIMODHistory
        documents={filterByDocumentType(documents, 'fbimod')}
        startEdit={startEdit}
        view={view}
        handleMyDelete={handleMyDelete}
      />
      <BNSQHistory documents={filterByDocumentType(documents, 'bnsq')} startEdit={startEdit} view={view} />
      <ADLHistory documents={filterByDocumentType(documents, 'adl')} startEdit={startEdit} view={view} />
      <IADLHistory
        documents={filterByDocumentType(documents, 'iadl')}
        startEdit={startEdit}
        view={view}
        handleMyDelete={handleMyDelete}
      />
    </React.Fragment>
  );
};

const DiagnosticSurvey = ({ documents }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const users = useAppSelector(s => s.session.orgUsers) ?? [];
  const mysqUserId = useAppSelector(s => s.myapp.mysqUserId);
  const { informants = [], delegates = [] } = useAppSelector(s => s.patient);
  const myAppDocuments = useAppSelector(s => s.myapp.sortedAndMergedDocuments);

  const surveyDocs = documents.filter(d => d._type !== 'diagnosis' && d._type !== 'mmse');

  const myFbiModDocs = filterDocumentsByType(myAppDocuments, ['fbimod']);
  const myIadlDocs = filterDocumentsByType(myAppDocuments, ['iadl']);

  // Surveys recorded in the clinic and by the patient/informant in the mobile app, as rendered in the history
  const allSurveyDocs = [...surveyDocs, ...myFbiModDocs, ...myIadlDocs];

  // My document handling
  const [myFormOpen, setMyFormOpen] = React.useState<
    | {
        doc: IDocumentWithTypeV2<TDiagnosticSurveyMyDocumentType>;
        type: 'edit' | 'view';
      }
    | undefined
  >(undefined);

  const [tempMyForm, setTempMyForm] = React.useState<IDocumentWithTypeV2<TDiagnosticSurveyMyDocumentType> | undefined>(
    undefined,
  );
  const [saving, setSaving] = React.useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Intercept navigation from SurveysHistory to open informant docs in StandAloneFormEditingHandler
  React.useEffect(() => {
    const state = location.state as { initData?: unknown; type: 'view' | 'edit' } | undefined;
    if (isMyDiagnosticSurveyDocument(state?.initData)) {
      setMyFormOpen({ doc: state.initData, type: state.type });
    }
  }, [location.state]);

  const saveMyForm = async () => {
    if (!myFormOpen) return;
    setSaving(true);
    await myAppActions.putNewCommit(myFormOpen.doc._type, myFormOpen.doc._id ?? '', tempMyForm, dispatch);
    setSaving(false);
    setTempMyForm(undefined);
  };

  // Seed the editable copy so that saving keeps the answers that were not touched
  React.useEffect(() => {
    if (myFormOpen) {
      setTempMyForm(structuredClone(myFormOpen.doc));
    }
  }, [myFormOpen]);

  const handleMyFormChange = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (value || value === 0) {
      setTempMyForm(prev => (prev ? { ...prev, [name]: value } : prev));
    }
  };

  const handleMyFormDelete = async (myDocId: string, docType: TMyDocumentType) => {
    await myAppActions.deleteDocument(docType, myDocId, dispatch);
  };

  if (myFormOpen) {
    const survEditor = surveyEditor(myFormOpen.doc, mysqUserId, users, delegates, informants, fm);
    return (
      <StandAloneFormEditingHandler
        currentPageName="diagnosticSurvey"
        headerLocaleId={
          myFormOpen.doc._type === 'fbimod' ? 'diagnosticSurvey.fbimod.title' : 'diagnosticSurvey.iadl.title'
        }
        headerInfoText={
          myFormOpen.doc._type === 'fbimod' ? 'diagnosticSurvey.fbimod.info' : 'diagnosticSurvey.iadl.info'
        }
        loading={saving}
        cancelEditingAction={() => {
          if (location.state && 'myFormDocType' in location.state) {
            navigate({ pathname: '/myService' });
          }
          setMyFormOpen(undefined);
          setTempMyForm(undefined);
        }}
        viewing={myFormOpen.type === 'view'}
        endViewingAction={() => {
          if (location.state && 'myFormDocType' in location.state) {
            navigate({ pathname: '/myService' });
          }
          setMyFormOpen(undefined);
        }}
        saveAction={async () => {
          setSaving(true);
          await saveMyForm();
          setMyFormOpen(undefined);
          setTempMyForm(undefined);
          setSaving(false);
        }}
      >
        <FormContextProvider
          context={{
            formData: { document: tempMyForm ?? myFormOpen.doc, onChange: handleMyFormChange },
            view:
              myFormOpen.type === 'view'
                ? { viewing: myFormOpen.doc._id, document: myFormOpen.doc, startView: () => () => null }
                : undefined,
          }}
        >
          <>
            <div style={{ marginBottom: '4rem' }}>
              <QuestionAnswerPair
                question={fm('myService.reporter')}
                questionSize={5}
                answer={surveyCreator(myFormOpen.doc, mysqUserId, users ?? [], [], informants, fm)}
              />
              {survEditor && (
                <QuestionAnswerPair question={fm('myService.edited')} answer={survEditor} questionSize={5} />
              )}
            </div>
            <DiagnosticSurveyForm document={myFormOpen.doc} />
          </>
        </FormContextProvider>
      </StandAloneFormEditingHandler>
    );
  }

  return (
    <FormEditingHandler name="diagnosticSurvey" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => {
        const startEditFunction: StartEdit<IDocumentWithTypeV2<TDiagnosticSurveyDocumentType>> = (document, name) =>
          isInformantSurvey(document, informants)
            ? () => setMyFormOpen({ doc: document, type: 'edit' })
            : startEdit(document, name);

        const startViewFunction: IView['startView'] = document => {
          const doc = allSurveyDocs.find(d => d._id === document._id);
          if (doc && isInformantSurvey(doc, informants)) {
            return () => setMyFormOpen({ doc, type: 'view' });
          }
          return view && document._id ? view.startView({ _id: document._id }) : () => null;
        };

        const type = docType(surveyDocs, editing, view);

        return (
          <DocumentWrapper>
            <DocumentHeader
              name={'diagnosticSurvey'}
              headerId={type ? `diagnosticSurvey.${type}.title` : 'diagnosticSurvey.title'}
              headerWidth={type === 'npi' ? 8 : undefined}
              info={
                type === 'npi' ? <InformationButton info={npiFormInformation.survey ?? []} size="3rem" /> : undefined
              }
              infoText={type ? `diagnosticSurvey.${type}.info` : undefined}
              editing={editing}
            />
            {editing || view?.viewing ? (
              <FormContextProvider context={{ formData, editing, view, documents }}>
                <DiagnosticSurveyForm document={surveyDocs.find(d => d._id === editing || d._id === view?.viewing)} />
              </FormContextProvider>
            ) : null}

            {!editing && !view?.viewing
              ? DiagnosticSurveyHistory(
                  allSurveyDocs,
                  startEditFunction,
                  handleMyFormDelete,
                  view ? { ...view, startView: startViewFunction } : undefined,
                )
              : undefined}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default DiagnosticSurvey;
